import {
  Box,
  Card,
  Grid,
  TextField,
  Typography,
  IconButton,
  Avatar,
} from "@mui/material";
import { Formik, Form } from "formik";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import GradientButton from "../Button";
import { useContext } from "react";
import { ResumeContext } from "../../ResumeContext";
import {
  saveResumeData,
  uploadResumeImage,
} from "../../api/resumeapi";
import BasicInfoSkeleton from "./skeletonLoader";

export default function BasicInfoForm({ onNext }) {
  const { data, setData, loading } = useContext(ResumeContext);

  if (loading) {
    return <BasicInfoSkeleton />;
  }

  if (!data) {
    return <BasicInfoSkeleton />;
  }

  const getImageSrc = (image) => {
    if (!image) return null;

    // New upload (File)
    if (image instanceof File) {
      return URL.createObjectURL(image);
    }

    // Backend image URL (string)
    if (typeof image === "string") {
      return image;
    }

    return null;
  };



  return (
    <Card sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
      <Typography fontSize={13} color="#6E6D81" mb={2}>
        Let's begin with you telling us a little about yourself!
      </Typography>

      <Formik
        enableReinitialize
        initialValues={{
          profileImage: data.imageUrl || null,
          firstName: data.fname || "",
          middleName: data.middleName || "",
          lastName: data.lname || "",
          headline: data.headline || "",
          summary: data.summary || "",
        }}
        onSubmit={async (values) => {
          const payload = {
            ...data,
            fname: values.firstName,
            lname: values.lastName,
            middleName: values.middleName,
            headline: values.headline,
            summary: values.summary,
          };

          try {
            await saveResumeData(payload);

            setData((prev) => ({
              ...prev,
              ...payload,
            }));

            if (values.profileImage) {
              await uploadResumeImage(data.userId, values.profileImage);
            }

            onNext();
          } catch (err) {
            console.error("Save Error:", err);
          }
        }}
      >
        {({ values, handleChange, setFieldValue }) => (
          <Form>
            <Grid container spacing={2}>
              {/* IMAGE */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Box
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    height: 200,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    position: "relative",
                  }}
                >
                  {!values.profileImage && (
                    <IconButton
                      component="label"
                      sx={{
                        background: "#1E88E5",
                        color: "#fff",
                        "&:hover": { background: "#1565C0" },
                      }}
                    >
                      <CloudUploadIcon />

                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setFieldValue("profileImage", file);
                          }
                        }}
                      />
                    </IconButton>
                  )}

                  {values.profileImage ? (
                    <Avatar
                      src={getImageSrc(values.profileImage)}
                      sx={{ width: 150, height: 150 }}
                    />
                  ) : (
                    <Typography color="textSecondary">
                      Upload Image
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* FIELDS */}
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={values.firstName}
                  onChange={handleChange}
                  size="small"
                />

                <TextField
                  fullWidth
                  label="Middle Name"
                  name="middleName"
                  value={values.middleName}
                  onChange={handleChange}
                  size="small"
                  sx={{ mt: 2 }}
                />

                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={values.lastName}
                  onChange={handleChange}
                  size="small"
                  sx={{ mt: 2 }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Headline"
                  name="headline"
                  value={values.headline}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Profile Summary"
                  name="summary"
                  value={values.summary}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <Box mt={3} display="flex" justifyContent="center" gap={4}>
              
              <GradientButton type="submit">Save & Next</GradientButton>
            </Box>
          </Form>
        )}
      </Formik>
    </Card>
  );
}
